/**
 * Inverse kinematics (docs/SKEL-EDITOR.md §5.1 `ik.ts`, report 01 §6.5), clean-room after
 * rusty_skelform 0.8 (MIT): FABRIK with a fixed 10 iterations (no tolerance), or the "Arc" solver
 * in the report's corrected form; then `pointBones` (each non-end joint aims at the next) and the
 * bend constraint (mirror every chain angle across the root→target line when the first joint is on
 * the wrong side). The solver only moves joint POSITIONS on a scratch copy of the FK pose and
 * returns absolute world rotations; `construct` re-runs FK with them. Families run in file order
 * on one shared scratch, as rusty does, so a later family sees earlier families' moved joints.
 * One deliberate addition: a (nearly) straight chain with a reachable target gets a small seed
 * bend before FABRIK (`seedStraightChain`), because FABRIK alone can never bend a collinear chain.
 *
 * A chain only looks right when every non-root member has local `pos = (segmentLength, 0)`
 * (bone-along-limb, D7): the second FK pass places each child on its parent's +x axis.
 */
import { add, angleOf, cross, dist, norm, rot, scale2, sub } from './math'
import type { Pose } from './animate'
import type { WorldBone } from './construct'
import type { IkConstraint, SkfRoot, V2 } from './types'

/**
 * FABRIK, one forward + one backward reach, in place on `pts` (chain order, root first).
 * Segment lengths are re-measured every pass exactly like rusty (measured before each move).
 */
export function fabrik(pts: V2[], root: V2, target: V2): void {
  const n = pts.length
  let next = target
  let length = 0
  for (let i = n - 1; i >= 0; i--) {
    const d = scale2(norm(sub(next, pts[i]!)), length)
    if (i > 0) length = dist(pts[i]!, pts[i - 1]!)
    pts[i] = sub(next, d)
    next = pts[i]!
  }
  let prev = root
  length = 0
  for (let i = 0; i < n; i++) {
    const d = scale2(norm(sub(prev, pts[i]!)), length)
    if (i < n - 1) length = dist(pts[i]!, pts[i + 1]!)
    pts[i] = sub(prev, d)
    prev = pts[i]!
  }
}

const ARC_EPS = 1e-6

/** A chain whose middle joints all lie within this fraction of its length of the root→target line counts as straight. */
const STRAIGHT_FRAC = 0.02
/** Seed bend (peak joint offset) given to a straight chain before FABRIK, as a fraction of its length. */
const SEED_FRAC = 0.05

/**
 * FABRIK cannot bend a chain that is (nearly) collinear with the root→target line: every reach
 * keeps the joints on that line, so with the target closer than the chain's length the end simply
 * never gets there (the leg stays straight and the foot leaves its target). Assembled rigs are
 * built from parts drawn hanging straight, so this is the FIRST thing a user hits when dragging a
 * new foot target up. When the chain is straight to within `STRAIGHT_FRAC` of its length and the
 * target is reachable, offset the middle joints on a small sine arch (`SEED_FRAC` of the length)
 * towards the side the constraint wants — `Clockwise` = counter-clockwise of root→target (knees
 * forward for a +x-facing leg), `CounterClockwise` the other side, `None` the side the chain already
 * leans to (CCW when exactly straight). In place; a no-op for bent chains, out-of-reach targets
 * and 2-joint chains, so SkelForm-authored poses solve exactly as rusty_skelform does.
 */
export function seedStraightChain(pts: V2[], root: V2, target: V2, constraint: IkConstraint | string): void {
  const n = pts.length
  if (n < 3) return
  const seg: number[] = []
  let total = 0
  for (let i = 1; i < n; i++) {
    const l = dist(pts[i]!, pts[i - 1]!)
    seg.push(l)
    total += l
  }
  const base = sub(target, root)
  const bl = Math.hypot(base.x, base.y)
  if (!(total > ARC_EPS) || bl < ARC_EPS * total || bl >= total * (1 - 1e-6)) return
  const u = { x: base.x / bl, y: base.y / bl }
  let maxOff = 0
  let sum = 0
  for (let i = 1; i < n - 1; i++) {
    const s = cross(u, sub(pts[i]!, root))
    maxOff = Math.max(maxOff, Math.abs(s))
    sum += s
  }
  if (maxOff >= STRAIGHT_FRAC * total) return
  const side = constraint === 'Clockwise' ? 1 : constraint === 'CounterClockwise' ? -1 : sum < 0 ? -1 : 1
  const perp = { x: -u.y * side, y: u.x * side }
  let acc = 0
  for (let i = 1; i < n - 1; i++) {
    acc += seg[i - 1]!
    const k = SEED_FRAC * total * Math.sin((Math.PI * acc) / total)
    pts[i] = { x: pts[i]!.x + perp.x * k, y: pts[i]!.y + perp.y * k }
  }
}

/**
 * Arc IK, corrected form (report 01 §6.5.3): joints on a sine arch between the root and the
 * clamped target, `local_i = (d_i·baseMag, (1 − peak)·sin(π·d_i)·baseMag)` rotated onto the
 * root→target line, where d_i = cumulative segment length / straight root→end distance. The
 * arch bulges clockwise of root→target (the constraint can mirror it). rusty's form uses the
 * joint's world x and the constant 3.14; this one is frame-independent. Guards a zero base.
 */
export function arcIk(pts: V2[], root: V2, target: V2): void {
  const n = pts.length
  if (n < 2) return
  const maxLen = dist(pts[n - 1]!, root)
  if (maxLen < ARC_EPS) return
  const d: number[] = [0]
  let acc = 0
  for (let i = 1; i < n; i++) {
    acc += dist(pts[i]!, pts[i - 1]!)
    d.push(acc / maxLen)
  }
  const base = sub(target, root)
  const baseAng = angleOf(base)
  const baseMag = Math.min(Math.hypot(base.x, base.y), maxLen)
  if (baseMag < ARC_EPS) return
  const peak = maxLen / baseMag
  for (let i = 1; i < n; i++) {
    const di = d[i]!
    const local = { x: di * baseMag, y: (1 - peak) * Math.sin(Math.PI * di) * baseMag }
    pts[i] = add(root, rot(local, baseAng))
  }
}

/** Absolute world rotation of each non-end joint, aiming at the next joint (rusty `point_bones`). */
export function pointBones(pts: V2[]): number[] {
  const rots = new Array<number>(pts.length).fill(0)
  let tip = pts[pts.length - 1]!
  for (let i = pts.length - 2; i >= 0; i--) {
    rots[i] = angleOf(sub(tip, pts[i]!))
    tip = pts[i]!
  }
  return rots
}

/**
 * Whether the constraint mirrors the chain: `cross(jointDir, baseDir) > 0` means the first joint
 * lies clockwise of root→target. `Clockwise` keeps it counter-clockwise (legs of a +x-facing
 * character bend forward), `CounterClockwise` the opposite (report 01 §6.5.4).
 */
export function constraintFlips(constraint: IkConstraint | string, root: V2, firstJoint: V2, target: V2): boolean {
  const c = cross(norm(sub(firstJoint, root)), norm(sub(target, root)))
  return (constraint === 'Clockwise' && c > 0) || (constraint === 'CounterClockwise' && c < 0)
}

/** Chain members of a family in array (= hierarchy) order, deduplicated and in range. */
export function chainOf(root: SkfRoot, familyId: number): number[] {
  const fam = root.inverse_kinematics[familyId]
  if (!fam) return []
  const n = root.bones.length
  return [...new Set(fam.bone_ids ?? [])].filter((id) => Number.isInteger(id) && id >= 0 && id < n).sort((a, b) => a - b)
}

/**
 * Solve every active family against the FK pose `world` (not mutated) and return the absolute
 * world rotations that override FK: non-end chain bones always, the end bone only with
 * `mimic_target` (it then copies the target's rotation). Mode/constraint/mimic come from the
 * animated `pose.ik` when present.
 */
export function solveIk(root: SkfRoot, world: WorldBone[], pose: Pose): Map<number, number> {
  const out = new Map<number, number>()
  const scratch = world.map((w) => ({ pos: { x: w.pos.x, y: w.pos.y }, rot: w.rot }))
  root.inverse_kinematics.forEach((fam, fi) => {
    const target = fam.target_id
    if (target < 0 || target >= scratch.length) return
    const chain = chainOf(root, fi)
    if (chain.length === 0) return
    const state = pose.ik[fi]
    const mode = state?.mode ?? fam.mode ?? 'FABRIK'
    const constraint = state?.constraint ?? fam.constraint ?? 'None'
    const mimic = state?.mimic_target ?? fam.mimic_target === true
    const last = chain.length - 1
    if (chain.length >= 2) {
      const rootPos = { ...scratch[chain[0]!]!.pos }
      const targetPos = { ...scratch[target]!.pos }
      const pts = chain.map((id) => ({ ...scratch[id]!.pos }))
      if (mode === 'Arc') arcIk(pts, rootPos, targetPos)
      else {
        seedStraightChain(pts, rootPos, targetPos, constraint)
        for (let it = 0; it < 10; it++) fabrik(pts, rootPos, targetPos)
      }
      pts.forEach((p, i) => {
        scratch[chain[i]!]!.pos = p
      })
      const rots = pointBones(pts)
      for (let i = 0; i < last; i++) scratch[chain[i]!]!.rot = rots[i]!
      if (constraintFlips(constraint, rootPos, pts[1]!, targetPos)) {
        const baseAng = angleOf(norm(sub(targetPos, rootPos)))
        for (const id of chain) scratch[id]!.rot = 2 * baseAng - scratch[id]!.rot
      }
      for (let i = 0; i < last; i++) out.set(chain[i]!, scratch[chain[i]!]!.rot)
    }
    if (mimic) out.set(chain[last]!, scratch[target]!.rot)
  })
  return out
}

export type IkRole = 'none' | 'root' | 'middle' | 'end' | 'target'

/**
 * A bone's role in IK (report 02 §13.2): chain `root` (transform editable), `middle`/`end` (the
 * editor disables their transform fields), `target` of some family, or `none`.
 */
export function ikRole(root: SkfRoot, boneId: number): IkRole {
  for (let fi = 0; fi < root.inverse_kinematics.length; fi++) {
    const chain = chainOf(root, fi)
    const at = chain.indexOf(boneId)
    if (at === 0) return 'root'
    if (at > 0) return at === chain.length - 1 ? 'end' : 'middle'
  }
  if (root.inverse_kinematics.some((f) => f.target_id === boneId)) return 'target'
  return 'none'
}
